import { Box, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  createCoachServicePackageRequest,
  deleteCoachServicePackageRequest,
  getCoachServicePackagesRequest,
  ICoachServicePackage,
  resetCoachServicePackages,
  updateCoachServicePackageRequest,
} from '../../redux/coach/coachSlice';
import CoachServicePackageEditCard, {
  ICoachServicePackageCardProps,
} from '../../components/AddCoachService/CoachServicePackageEditCard';
import CoachServicePackageCard from '../../components/AddCoachService/CoachServicePackageCard';
import AddNewCoachPackage from '../../components/AddCoachService/AddNewCoachPackage';
import ServiceCardLoading from '../../components/AddCoachService/SkeletonServiceCard';

function Packages() {
  const dispatch = useAppDispatch();
  const coachId = useAppSelector((state) => state.coach.coach?.id);
  const {
    coachServicePackages: services,
    addingNewPackageSuccess,
    updatingNewPackageSuccess,
    coachServicePackagesLoading,
  } = useAppSelector((state) => state.coach);
  const [packages, setPackages] = useState<ICoachServicePackage[]>([]);
  const [isAddingNewPackage, setIsAddingNewPackage] = useState(false);
  const [editModePackageId, setEditModePackageId] = useState<string | null>(null);
  const [deleteModePackageId, setDeleteModePackageId] = useState<string | null | undefined>(null);

  useEffect(() => {
    setPackages(services);
  }, [services]);

  useEffect(() => {
    if (coachId) {
      dispatch(getCoachServicePackagesRequest({ coachId }));
    }
  }, [coachId]);

  useEffect(() => {
    if (addingNewPackageSuccess || updatingNewPackageSuccess) {
      if (addingNewPackageSuccess) {
        setIsAddingNewPackage(false);
      }
      if (updatingNewPackageSuccess) {
        setEditModePackageId(null);
      }
      dispatch(resetCoachServicePackages());
    }
  }, [addingNewPackageSuccess, updatingNewPackageSuccess]);

  const handleAddNewPackageClick = () => {
    setIsAddingNewPackage(true);
    setEditModePackageId(null);
    setDeleteModePackageId(null);
  };

  const handleEditPackage = (pkg: ICoachServicePackageCardProps) => {
    if (pkg.id) {
      setEditModePackageId(pkg.id);
      setIsAddingNewPackage(false);
      setDeleteModePackageId(null);
    }
  };

  const handleUpdatePackage = (pkg: ICoachServicePackageCardProps) => {
    if (pkg.id) {
      const updatedPackage = {
        id: pkg.id,
        title: pkg.title,
        bookingType: pkg.bookingType,
        sessionPrice: pkg.rate,
        sessionDuration: pkg.duration,
        sessionDescription: pkg.description,
        coachId: pkg.coachId,
      };
      dispatch(updateCoachServicePackageRequest(updatedPackage));
    }
  };

  const addPackage = (newPackage: ICoachServicePackageCardProps) => {
    const newConvertedPackage = {
      title: newPackage.title,
      bookingType: newPackage.bookingType,
      sessionPrice: newPackage.rate,
      sessionDuration: newPackage.duration,
      sessionDescription: newPackage.description,
      coachId: newPackage.coachId,
    };
    dispatch(createCoachServicePackageRequest(newConvertedPackage));
  };

  const handleDelete = (id: string) => {
    dispatch(deleteCoachServicePackageRequest({ id, coachId }));
  };

  if (coachServicePackagesLoading) {
    return (
      <Grid
        display="flex"
        flexWrap="wrap"
        justifyContent="flex-start"
        alignContent="flex-start"
        alignItems="center"
        width="100%"
        gap={1}
        borderRadius="10px"
        height="79vh"
        py={3.5}
        px={0.5}
        sx={{
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '2px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(93,171,201,0.5)',
            borderRadius: '10px',
          },
          '@media (max-width: 920px)': {
            gap: '20px',
            justifyContent: 'center',
            alignItems: 'center',
          },
          '@media (max-width: 800px)': {
            gap: '10px',
            justifyContent: 'center',
            alignItems: 'center',
            alignContent: 'center',
          },
        }}
      >
        <ServiceCardLoading />
        <ServiceCardLoading backgroundColor="#fff" />
        <ServiceCardLoading backgroundColor="#fff" />
        <ServiceCardLoading backgroundColor="#fff" />
        <ServiceCardLoading backgroundColor="#fff" />
      </Grid>
    );
  }

  return (
    <Grid
      display="flex"
      flexWrap="wrap"
      justifyContent="flex-start"
      alignContent="flex-start"
      alignItems="flex-start"
      gap={1.4}
      borderRadius="10px"
      width="100%"
      height="79vh"
      py={3.5}
      px={0.5}
      sx={{
        overflowX: 'hidden',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(93,171,201,0.5)',
          borderRadius: '10px',
        },
        '@media (max-width: 800px)': {
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
    >
      {isAddingNewPackage ? (
        <CoachServicePackageEditCard
          addPackage={addPackage}
          initialPackage={null}
          onDismiss={() => setIsAddingNewPackage(false)}
          backgroundColor="#fff"
        />
      ) : (
        <AddNewCoachPackage onClick={handleAddNewPackageClick} height="500px" />
      )}
      {packages.map((pkg) =>
        pkg.id === editModePackageId ? (
          <CoachServicePackageEditCard
            key={pkg.id}
            initialPackage={{
              ...pkg,
              title: pkg.title,
              bookingType: pkg.bookingType,
              rate: pkg.sessionPrice,
              duration: pkg.sessionDuration,
              description: pkg.sessionDescription,
            }}
            updatePackage={handleUpdatePackage}
            onDismiss={() => setEditModePackageId(null)}
            backgroundColor="#fff"
          />
        ) : (
          <CoachServicePackageCard
            key={pkg.id}
            {...pkg}
            title={pkg.title}
            bookingType={pkg.bookingType}
            rate={pkg.sessionPrice}
            duration={pkg.sessionDuration}
            description={pkg.sessionDescription}
            onEdit={() =>
              handleEditPackage({
                ...pkg,
                title: pkg.title,
                bookingType: pkg.bookingType,
                rate: pkg.sessionPrice,
                duration: pkg.sessionDuration,
                description: pkg.sessionDescription,
              })
            }
            onDelete={() => handleDelete(pkg.id ?? '')}
            backgroundColor="#fff"
            setDeleteModePackageId={setDeleteModePackageId}
            deleteModePackageId={deleteModePackageId}
            setEditModePackageId={setEditModePackageId}
            editModePackageId={editModePackageId}
          />
        )
      )}
    </Grid>
  );
}

export default Packages;
